import { Button } from "@/src/components/atom/Button";
import Dialog from "@/src/components/atom/Dialog";
import DialogFooterContainer from "@/src/components/atom/Dialog/DialogFooterContainer";
import CancelAlertDialog from "@/src/components/molecule/CancelAlertDialog";
import ContactFormItem from "@/src/components/molecule/ContactFormItem";
import FormItem from "@/src/components/molecule/FormItem";
import { authApi } from "@/src/store/apis/auth";
import { GenerateBuyerEmployeeDto } from "@/src/store/apis/client/buyer/interface";
import { isUndefined } from "@/src/utils/is";
import { emailRegex } from "@/src/utils/regex";
import { transformMaskPhoneNumber } from "@/src/utils/transform";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";

interface BuyerEmployeeEditDialogProps {
  name: string;
  email: string;
  isActivated: string;
  personalContact?: string;
  officeContact?: string;
  personalContactPrefix?: string;
  officeContactPrefix?: string;
  buyerEmployeeRemark?: string;
  onSave: (values: GenerateBuyerEmployeeDto) => void;
  onClose: () => void;
}

const DEFAULT_VALUE = {
  name: "",
  email: "",
  personalContact: "",
  officeContact: "",
  isActivated: "Y",
  buyerEmployeeRemark: "",
};

const BuyerEmployeeEditDialog = ({
  name,
  email,
  personalContact,
  officeContact,
  personalContactPrefix,
  officeContactPrefix,
  isActivated,
  buyerEmployeeRemark,
  onClose,
  onSave,
}: BuyerEmployeeEditDialogProps) => {
  const { t } = useTranslation();
  const { handleSubmit, control, register, setValue, setFocus, watch } =
    useForm({
      mode: "onBlur",
      reValidateMode: "onBlur",
      defaultValues: {
        ...DEFAULT_VALUE,
        ...{
          name,
          email,
          personalContact,
          officeContact,
          personalContactPrefix,
          officeContactPrefix,
          isActivated,
          buyerEmployeeRemark,
        },
      },
    });
  const ref = useRef<HTMLButtonElement | null>(null);
  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState<boolean>(false);

  const { isManager } = authApi.endpoints.getSession.useQueryState(undefined, {
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;

      return {
        isManager: isStable
          ? currentData.row.exporterUserType === "MANAGER"
          : false,
      };
    },
  });

  useEffect(() => {
    if (isManager) {
      setValue("personalContact", transformMaskPhoneNumber(personalContact));
    }
  }, [isManager, personalContact, setValue]);

  useEffect(() => {
    setFocus("name");
  }, [setFocus]);

  return (
    <Dialog
      title={t("clientManagement:exporter.buyerListAddPage.editContactPerson")}
      open
      onOpenChange={onClose}
      destroyDialogWhenEscapePress={false}
      onEscapeKeyDown={() => setIsCancelAlertOpen(true)}
      footer={
        <DialogFooterContainer>
          <Button
            buttonColor="black"
            buttonGrade="tertiary"
            onClick={() => setIsCancelAlertOpen(true)}
          >
            {t("clientManagement:exporter.common.exit")}
          </Button>
          <Button onClick={() => ref.current?.click()}>
            {t("clientManagement:exporter.common.save")}
          </Button>
        </DialogFooterContainer>
      }
    >
      <Form
        onSubmit={handleSubmit((data) =>
          onSave({
            ...data,
            isActivated: data.isActivated === "Y",
            personalContact: isManager ? personalContact : data.personalContact,
          })
        )}
      >
        <FormItem
          type="text"
          direction="vertical"
          label={t("clientManagement:exporter.common.name")}
          control={control}
          name="name"
          rules={{ required: true }}
          inputProps={{
            placeholder: t("clientManagement:exporter.placeholder.name"),
          }}
          errorsMessage={{
            required: t("error:required"),
          }}
        />
        <FormItem
          type="text"
          direction="vertical"
          label={t("clientManagement:exporter.common.email")}
          control={control}
          name="email"
          rules={{ required: true, pattern: emailRegex }}
          inputProps={{
            placeholder: t("clientManagement:exporter.placeholder.email"),
          }}
          errorsMessage={{
            required: t("error:required"),
            pattern: t("error:login.emailTypeMissMatching"),
          }}
        />
        <ContactFormItem
          register={register}
          setValue={setValue}
          label={t("clientManagement:exporter.common.personalContact")}
          direction="vertical"
          prefixName="personalContactPrefix"
          restContactName="personalContact"
          prefixValue={watch("personalContactPrefix") || undefined}
          restContactValue={watch("personalContact") || ""}
          isRequired={false}
          disabled={isManager}
        />
        <ContactFormItem
          register={register}
          setValue={setValue}
          label={t("clientManagement:exporter.common.officeContact")}
          direction="vertical"
          prefixName="officeContactPrefix"
          restContactName="officeContact"
          prefixValue={watch("officeContactPrefix") || undefined}
          restContactValue={watch("officeContact") || ""}
          isRequired={false}
        />
        <FormItem
          type="radioGroup"
          direction="vertical"
          label={t("clientManagement:exporter.common.use")}
          control={control}
          name="isActivated"
          rules={{ required: true }}
          options={[
            { label: "Y", value: "Y" },
            { label: "N", value: "N" },
          ]}
          errorsMessage={{
            required: t("error:required"),
            pattern: t("error:login.emailTypeMissMatching"),
          }}
        />

        <FormItem
          type="textarea"
          name="buyerEmployeeRemark"
          direction="vertical"
          label={t("clientManagement:exporter.common.remark")}
          control={control}
          inputProps={{
            style: { width: "100%", height: "150px" },
            placeholder: t("clientManagement:exporter.placeholder.remark"),
            maxLength: 100,
          }}
        />

        {isCancelAlertOpen && (
          <CancelAlertDialog
            open={isCancelAlertOpen}
            onOpenChange={setIsCancelAlertOpen}
            onOk={() => {
              onClose();
              setIsCancelAlertOpen(false);
            }}
          />
        )}

        <Hidden type="submit" ref={ref} />
      </Form>
    </Dialog>
  );
};

export default BuyerEmployeeEditDialog;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Hidden = styled.button`
  position: absolute;
  width: 0;
  height: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
`;
